// Debounced item_change for a HEADER form — the behaviour <TxnScreen>'s entry
// form has always had, shared so every surface that edits a record through
// the shared FormRenderer behaves the same: <TxnScreen> and the approvals
// inbox's Edit mode (which used to only store the typed value, so a Vision
// bean's derived fields / protect state never arrived while editing a staged
// record).
//
// Contract (the server's ItemChangeResult): `updates` merge into the form,
// `protect` (1 lock / 0 unlock) merges into the dynamic-protect map, warnings
// and prompts toast, an `error` toasts and still applies any `updates` it
// carries (a blocking handler clears the fields it derives). 700ms after the
// last change — each change resets the timer.
//
// The record's mode rides along as `edit_mode` ('add' | 'edit') so the
// server can hand a Vision bean the right editFlag ('A' / 'E').
//
// Unlike list/useItemChange (child grids, best-effort and silent), a header
// item change reports its failures — the user is looking at the form.

import { useCallback, useEffect, useMemo, useRef } from 'react'
import type { Dispatch, SetStateAction } from 'react'

import { columnFormKey } from '../utils'
import { normalizeItemChange } from '../types'
import type { ItemChangeFormChange, SqlModel } from '../types'
import type { TxnNotifier } from '../context'

interface HeaderItemChangeResponse {
  ok: boolean
  updates?: Record<string, unknown>
  error?: string
  protect?: Record<string, 0 | 1>
  warning?: string
  warnings?: Array<{ message: string; code?: string | null }>
  prompts?: Array<{ message: string; code?: string | null }>
  forms?: Record<string, ItemChangeFormChange>
}

export interface UseHeaderItemChangeOpts {
  /** The execute funnel (TxnScreenRuntime / ApprovalsInboxRuntime). */
  execute: (objName: string, action: string, payload?: Record<string, unknown>) => Promise<unknown>
  notify: TxnNotifier
  objName: string
  sm: SqlModel | null | undefined
  setFormValues: Dispatch<SetStateAction<Record<string, unknown>>>
  setDynamicProtect: Dispatch<SetStateAction<Record<string, 0 | 1>>>
  /** Whether the record is new or opened — read when the call fires. */
  editMode?: () => 'add' | 'edit' | undefined
  /** Failed request reporter; defaults to a toast. */
  reportError?: (context: string, err: unknown) => void
  debounceMs?: number
  /** The header's form_no — a multi-form result addresses it by number. */
  headerFormNo?: string
  /** Child-form rows a multi-form result carries (header entry excluded by
   *  the receiver). Omit on surfaces that hold no child buffer. */
  onFormRows?: (forms: Record<string, ItemChangeFormChange> | undefined, headerNo: string) => void
}

/** What one immediate item_change produced (see `trigger.now`). */
export interface HeaderItemChangeOutcome {
  /** False when the column has no invokable item_change — nothing was sent. */
  ran: boolean
  /** False when the handler returned an error or the request failed. */
  ok: boolean
  /** The values applied to the form (the result's updates + header `set`). */
  updates: Record<string, unknown>
  error?: string
}

/** The debounced trigger, plus `now` — the same call run IMMEDIATELY and
 *  awaitable, for a caller that sets several columns in order and must let
 *  each one's item_change land before the next (an 'open' action button). */
export type HeaderItemChangeTrigger = ((fieldName: string, header: Record<string, unknown>) => void) & {
  now: (fieldName: string, header: Record<string, unknown>) => Promise<HeaderItemChangeOutcome>
}

/** Returns `trigger(fieldName, header)` — `fieldName` is the column's form_key
 *  (or raw name), `header` the form's values INCLUDING the change. */
export function useHeaderItemChange(opts: UseHeaderItemChangeOpts): HeaderItemChangeTrigger {
  const {
    execute, notify, objName, sm, setFormValues, setDynamicProtect,
    editMode, reportError, debounceMs = 700, headerFormNo = '1', onFormRows,
  } = opts
  const timer = useRef<number | null>(null)
  // Read at fire time so a stale closure never sends the previous mode.
  const editModeRef = useRef(editMode)
  useEffect(() => { editModeRef.current = editMode }, [editMode])
  useEffect(() => () => {
    if (timer.current) window.clearTimeout(timer.current)
  }, [])

  // Whether this column has an item_change worth a round-trip.
  const invokable = useCallback((fieldName: string): boolean => {
    const col = sm?.columns?.find(w => {
      const c = w.column
      if (!c) return false
      return c.name === fieldName || columnFormKey(c) === fieldName
    })?.column
    if (!col) return false
    const ic = normalizeItemChange(col.item_change)
    if (!ic) return false
    // Skip empty configurations so we don't burn round-trips on no-op
    // handlers. A rule is invokable once it has fills or protect entries.
    if (ic.mode === 'code' && !ic.code.trim()) return false
    if (ic.mode === 'rule'
      && (!ic.fill || ic.fill.length === 0)
      && (!ic.protect || ic.protect.length === 0)) return false
    return true
  }, [sm])

  // One item_change call, applied to the form; resolves with what it applied.
  const runOnce = useCallback(
    async (fieldName: string, header: Record<string, unknown>): Promise<HeaderItemChangeOutcome> => {
      const notifyInfo = notify.info ?? notify.success
      const notifyWarn = notify.warn ?? notifyInfo
      try {
        const mode = editModeRef.current?.()
        const res = await execute(objName, 'item_change', {
          field_name: fieldName,
          header,
          // Header form has no parent; expression {header.X} resolves to none.
          header_values: {},
          ...(mode ? { edit_mode: mode } : {}),
        }) as HeaderItemChangeResponse
        if (res?.error) {
          notify.error(`item_change failed: ${res.error}`)
          // A blocking handler may still return updates so the fields it
          // derives are CLEARED rather than left showing stale values.
          const errUpdates = res?.updates || {}
          if (Object.keys(errUpdates).length > 0) {
            setFormValues(prev => ({ ...prev, ...errUpdates }))
          }
          return { ran: true, ok: false, updates: errUpdates, error: res.error }
        }
        if (res?.warnings?.length) res.warnings.forEach(w => notifyWarn(w.message))
        else if (res?.warning) notifyWarn(res.warning)
        res?.prompts?.forEach(p => notifyInfo(p.message))
        // Applied before the updates early-return so a protect-only
        // response still takes effect.
        // A multi-form return addresses the header by its form_no too.
        const headerChange = res?.forms?.[headerFormNo]
        const protect = { ...(res?.protect || {}), ...(headerChange?.protect || {}) }
        if (Object.keys(protect).length > 0) {
          setDynamicProtect(prev => ({ ...prev, ...protect }))
        }
        onFormRows?.(res?.forms, headerFormNo)
        const updates = { ...(res?.updates || {}), ...(headerChange?.set || {}) }
        if (Object.keys(updates).length > 0) {
          setFormValues(prev => ({ ...prev, ...updates }))
        }
        return { ran: true, ok: true, updates }
      } catch (err) {
        if (reportError) reportError('item_change failed', err)
        else notify.error(`item_change failed: ${err instanceof Error ? err.message : String(err)}`)
        return {
          ran: true, ok: false, updates: {},
          error: err instanceof Error ? err.message : String(err),
        }
      }
    },
    [execute, notify, objName, setFormValues, setDynamicProtect, reportError,
      headerFormNo, onFormRows],
  )

  return useMemo(() => {
    const trigger = (fieldName: string, header: Record<string, unknown>) => {
      if (!invokable(fieldName)) return
      if (timer.current) window.clearTimeout(timer.current)
      timer.current = window.setTimeout(() => { void runOnce(fieldName, header) }, debounceMs)
    }
    const now = async (fieldName: string, header: Record<string, unknown>) => {
      if (!invokable(fieldName)) return { ran: false, ok: true, updates: {} }
      return runOnce(fieldName, header)
    }
    return Object.assign(trigger, { now })
  }, [invokable, runOnce, debounceMs])
}
